import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getProductTravelList,
  addTravelToProduct,
  updateProductTravel,
  deleteProductTravel,
  getBusinessTravelCountryData,
} from "../../../../../../apis/Product/BusinessTravelApi";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const ProdTravelTable = ({ productId, method = "manual" }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownCountries, setDropdownCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Transportation means options for Flow 14.1
  const transportationMeans = useMemo(
    () => [
      {
        value: t("LAND_TRANSPORTATION_SERVICES"),
        id: "land_transportation",
      },
      {
        value: t("RAILWAY_TRANSPORTATION_SERVICES"),
        id: "railway_transportation",
      },
      {
        value: t("SEA_AND_COASTAL_WATER_TRANSPORTATION_SERVICES"),
        id: "sea_coastal_water",
      },
      {
        value: t("INLAND_WATER_TRANSPORTATION_SERVICES"),
        id: "inland_water",
      },
      {
        value: t("AIR_TRANSPORT_SERVICES"),
        id: "air_transport",
      },
    ],
    [t]
  );

  // Dynamic currency options based on selected country
  const currencyOptions = useMemo(() => {
    if (!selectedCountry) return [];

    return [
      {
        value: selectedCountry.currency_name,
        id: selectedCountry.currency_code,
        currency_code: selectedCountry.currency_code,
        currency_name: selectedCountry.currency_name,
        exchange_rate: selectedCountry.exchange_rate,
        inflation_rate: selectedCountry.inflation_rate,
        emission_factor: selectedCountry.emission_factor,
      },
    ];
  }, [selectedCountry]);

  // Dynamic placeholders for country to currency mapping
  const dynamicPlaceholders = useMemo(
    () => ({
      visiting_country: {
        targetField: "currency",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedCountry = dropdownData?.find(
            (option) => option?.value === selectedValue
          );
          if (selectedCountry) {
            setSelectedCountry(selectedCountry);
            return {
              value: selectedCountry.currency_name,
              id: selectedCountry.currency_code,
              currency_code: selectedCountry.currency_code,
              currency_name: selectedCountry.currency_name,
              exchange_rate: selectedCountry.exchange_rate,
              inflation_rate: selectedCountry.inflation_rate,
              emission_factor: selectedCountry.emission_factor,
            };
          }
          return null;
        },
        dropdownDataKey: "visiting_country",
      },
    }),
    []
  );

  useEffect(() => {
    getBusinessTravelCountryData({
      setDropdown: setDropdownCountries,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const getFieldValue = useCallback((field) => {
    if (!field) return null;
    if (typeof field === "object") {
      // For dropdown fields, use the id if available, otherwise use value
      return field.id || field.value;
    }
    return field;
  }, []);

  // Function to convert identifier back to display object for UI
  const convertIdentifierToDisplay = useCallback(
    (field, fieldName) => {
      if (!field) return null;

      if (fieldName === "means_transportation") {
        const option = transportationMeans.find((item) => item.id === field);
        return option ? { value: option.value, id: option.id } : field;
      }

      if (fieldName === "currency") {
        const option = currencyOptions.find((item) => item.id === field);
        return option ? { value: option.value, id: option.id } : field;
      }

      return field;
    },
    [transportationMeans, currencyOptions]
  );

  const fetchData = useCallback(() => {
    getProductTravelList({
      productId,
      setObjects: (fetchedData) => {
        // Filter data by method
        const filteredData = fetchedData.filter(
          (item) => item.method === method
        );

        // Convert identifiers back to display objects for UI
        const convertedData = filteredData.map((item) => ({
          ...item,
          means_transportation: convertIdentifierToDisplay(
            item.means_transportation,
            "means_transportation"
          ),
          currency: convertIdentifierToDisplay(item.currency, "currency"),
        }));

        setData(convertedData);
      },
    });
  }, [productId, method, convertIdentifierToDisplay]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(
    (editedItem) => {
      // Find the original item to get the current currency if not changed
      const originalItem = data.find((item) => item.id === editedItem.id);

      // If currency is not in editedItem (user didn't touch currency field), use original
      let currencyValue = null;
      if (editedItem.hasOwnProperty("currency")) {
        // User edited currency field
        currencyValue =
          editedItem.currency?.currency_code || editedItem.currency?.id || null;
      } else {
        // User didn't touch currency field, use original
        currencyValue =
          originalItem?.currency?.currency_code ||
          originalItem?.currency?.id ||
          null;
      }

      // For invoice method, currency is required
      if (method === "invoice" && !currencyValue) {
        console.error(
          "Currency is required for invoice method in ProductTravel"
        );
        return;
      }

      updateProductTravel({
        productId,
        componentId: editedItem.id,
        method: method,
        ...editedItem,
        currency: currencyValue,
        visiting_country: getFieldValue(editedItem.visiting_country),
        means_transportation: getFieldValue(editedItem.means_transportation),
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [productId, method, data, setData, getFieldValue]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const currencyValue =
        newItem.currency?.currency_code || newItem.currency?.id || null;

      // For invoice method, currency is required
      if (method === "invoice" && !currencyValue) {
        console.error(
          "Currency is required for invoice method in ProductTravel"
        );
        return;
      }

      addTravelToProduct({
        productId,
        method: method,
        ...newItem,
        visiting_country: getFieldValue(newItem.visiting_country),
        means_transportation: getFieldValue(newItem.means_transportation),
        currency: currencyValue,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, method, fetchData, getFieldValue]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteProductTravel({
        productId,
        componentId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  // Flow 14.1: Invoice Method columns
  const invoiceColumns = useMemo(
    () => [
      {
        accessor: "name_of_journey",
        header: t("NAME_OF_JOURNEY"),
        type: "text",
        disableSortBy: true,
        width: "150px",
      },
      {
        accessor: "visiting_country",
        header: `${t("VISITING_COUNTRY")} *`,
        type: "dropdown",
        dropdownData: dropdownCountries,
        disableSortBy: true,
        width: "120px",
      },
      {
        accessor: "currency",
        header: `${t("CURRENCY")} *`,
        type: "dropdown",
        dropdownData: currencyOptions,
        disableSortBy: true,
        width: "100px",
        disabled: !selectedCountry,
      },
      {
        accessor: "expenditures_hotel_restaurant",
        header: t("EXPENDITURES_HOTEL_RESTAURANT"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        optional: true,
      },
      {
        accessor: "means_transportation",
        header: t("MEANS_OF_TRANSPORTATION"),
        type: "dropdown",
        dropdownData: transportationMeans,
        disableSortBy: true,
        width: "150px",
        optional: true,
      },
      {
        accessor: "expenditures_transportation",
        header: t("EXPENDITURES_TRANSPORTATION"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        optional: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "100px",
      },
    ],
    [
      dropdownCountries,
      currencyOptions,
      transportationMeans,
      t,
      selectedCountry,
    ]
  );

  // Flow 14.2: Manual Method columns
  const manualColumns = useMemo(
    () => [
      {
        accessor: "name_of_journey",
        header: t("NAME_OF_JOURNEY"),
        type: "text",
        disableSortBy: true,
        width: "150px",
      },
      {
        accessor: "visiting_country",
        header: `${t("VISITING_COUNTRY")} *`,
        type: "dropdown",
        dropdownData: dropdownCountries,
        disableSortBy: true,
        width: "120px",
      },
      {
        accessor: "quantity_per_functional_unit",
        header: `${t("QUANTITY_PER_FUNCTIONAL_UNIT")} *`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "150px",
        tipHeader: "Tip",
        tipBody: t("TIP_QUANTITY_PER_FUNCTIONAL_UNIT_TRAVEL"),
      },
      {
        accessor: "manuel",
        header: `${t("MANUAL_KGCO2E")} *`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        tipHeader: "Tip",
        tipBody: t("TIP_MANUAL_KGCO2E_TRAVEL"),
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "100px",
      },
    ],
    [dropdownCountries, t]
  );

  const columns = method === "invoice" ? invoiceColumns : manualColumns;

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      visiting_country: dropdownCountries,
      means_transportation: transportationMeans,
      currency: currencyOptions,
    }),
    [dropdownCountries, transportationMeans, currencyOptions]
  );

  const disableRules = {
    carbon: () => true,
  };

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default ProdTravelTable;
